import React, { useEffect, useState } from 'react';
import styles from 'components/medicalHistory/page.module.scss';
import Button from 'components/common/button';
import UploaderElement from 'components/common/uploaderElement';
import {
  StateTypeLocalStorage,
  usePersistMHReducer,
} from 'reducer/medicalHistory';
import { NullableCommunique } from 'types/types';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import MetaTag from 'components/meta';
import Label from 'components/common/label';
import useMediaUpload from 'hooks/useMediaUpload';
import { getAllowedFileTypesByCategory } from 'utils/files';
import { MultimediaType } from 'utils/constants';
import { isEmptyString } from 'utils/helper';
import Spinner from 'components/spinner/Spinner';
import MediaPreview from 'components/common/MediaPreview';
import { MultiMediaResponse } from 'artifacts/dtos';
import { GetPatientMultimediaByTypePatientIdMultimediaType } from 'hooks/useApi';

type Props = {
  nextPage: number;
  currentPage: number;
  actionPage: string;
  nextOrSubmitButton: (nextPage: number, submit?: boolean) => void;
};

function PhotoId({
  nextPage,
  nextOrSubmitButton,
  actionPage,
}: Props) {
  const { mediaUpload } = useMediaUpload();
  const communiqueData = useSelector(
    (state: RootState) => state.communique,
  ) as NullableCommunique;

  const { data, isLoading, refetch } = GetPatientMultimediaByTypePatientIdMultimediaType({
    patientId: communiqueData?.patientId ?? 0,
    multimediaType: MultimediaType.Identification,
  });

  const [idPhoto, setIdPhoto] = useState('');
  const [state, dispatch] = usePersistMHReducer();

  const clickNextOrSubmit = async (nextPage: number) => {
    const payload = {
      idPhoto,
    };

    if (!isEmptyString(idPhoto)) {
      mediaUpload({
        patientId: communiqueData?.patientId,
        dataUrl: idPhoto,
        multimediaType: MultimediaType.Identification,
        extraName: 'Photo-id',
      });
    }

    await dispatch({
      type: actionPage,
      name: 'Photo ID',
      value: payload,
    });

    nextOrSubmitButton(nextPage);
  };

  const onChangeIdPhoto = (value: string) => {
    setIdPhoto(value);
  };

  // Back to state values
  useEffect(() => {
    if (state) {
      const getPhotoId = state.find(
        (page: StateTypeLocalStorage) => page.name === actionPage,
      );
      if (Object.keys(getPhotoId?.value).length) {
        setIdPhoto(getPhotoId.value.idPhoto);
      }
    }
  }, [state]);

  return (
    <>
      <MetaTag title="Photo ID" />
      <div className="mb-3 pb-3">
        <div>
          <h2 className={`marginTopZero ${styles.titleText}`}>Photo ID</h2>
          <div className="mb-4">
            <Label>
              Please, upload a picture of of your
              {' '}
              <span className="bold">photo ID</span>
            </Label>
            <p className={styles.separator} />
            <UploaderElement
              getImage={onChangeIdPhoto}
              imageBase64={idPhoto}
              acceptExtensions={getAllowedFileTypesByCategory(MultimediaType.ProfilePicture)}
              disabled={isLoading}
            />
          </div>
          <div className="text-base font-semibold my-4">
            Photo IDs
          </div>
          {isLoading && (
            <div className="flex flex-col justify-start items-center text-xs">
              <Spinner />
              Loading photo ID...
            </div>
          )}
          {!isLoading && data?.length === 0 && (
            <div className="text-xs text-gray-500">
              No photo ID found
            </div>
          )}
          {data?.map((media: MultiMediaResponse) => (
            <MediaPreview
              key={media.url?.value}
              thumbnail
              media={media}
              refetch={async () => { await refetch(); }}
              hasDelete={false}
              className="!my-4"
            />
          ))}
        </div>
        <div className="next-button-fixed">
          <Button onClick={() => clickNextOrSubmit(nextPage)}>
            Next
          </Button>
        </div>
      </div>
    </>
  );
}

export default PhotoId;
